import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChannelStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, enumOptions, SelectFilterField, TextFilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { MerchantCell } from "@/components/merchant-cell";
import { CHANNEL_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { formatStatusLabel } from "@/lib/utils";
import { listChannels } from "@/api/channels";
import { listMerchants } from "@/api/merchants";
import { getInvoiceAssetOptions } from "@/api/invoices";
import { type ChannelListItem, type ListChannelsParams } from "@/api/types";

// Task 14 (feature-031 Phase B): platform-wide Channels list — mirrors
// _app.invoices.tsx's list-page shape (Task 26) exactly: useTableState for
// URL-persisted page/sort/filters, per-column filter popovers for
// status/merchant/asset/search. The merchant/asset filter
// options reuse listMerchants/getInvoiceAssetOptions (MERCHANTS_VIEWER /
// INVOICES_VIEWER reads respectively) — same graceful-degradation precedent
// _app.invoices.tsx already relies on: a caller who lacks that specific role
// simply sees an empty dropdown, not a broken page (this page itself is
// gated at CHANNELS_VIEWER by the shell's nav).
type ChannelFilters = {
  status?: ChannelStatus;
  merchantId?: string;
  assetId?: string;
  search?: string;
};

const DEFAULT_FILTERS: ChannelFilters = {};

function validateChannelsSearch(search: Record<string, unknown>): TableSearch<ChannelFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as ChannelStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    assetId: typeof search.assetId === "string" ? search.assetId : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
  };
}

export const Route = createFileRoute("/_app/channels")({
  validateSearch: validateChannelsSearch,
  component: ChannelsPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function RowLink({ channelId, children }: { channelId: string; children: ReactNode }) {
  return (
    <Link to="/channels/$channelId" params={{ channelId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

function ChannelsPage() {
  const tableState = useTableState<ChannelFilters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });

  const params: ListChannelsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: (tableState.sortBy ?? undefined) as ListChannelsParams["sortBy"],
    sortOrder: tableState.sortOrder,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["channels", params],
    queryFn: () => listChannels(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const { data: assetOptions } = useQuery({
    queryKey: ["invoice-asset-options"],
    queryFn: getInvoiceAssetOptions,
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<ChannelListItem>[] = [
    {
      key: "publicId",
      header: "Channel ID",
      filter: columnFilter(tableState, ["search"], (draft, set) => (
        <TextFilterField
          id="filter-search"
          label="Search"
          placeholder="Public ID, external ID, or label"
          value={draft.search}
          onChange={(v) => set("search", v)}
        />
      )),
      cell: (c) => (
        <RowLink channelId={c.id}>
          <span className="font-mono group-hover:underline">{c.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "merchant",
      header: "Merchant",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <SelectFilterField
          id="filter-merchant"
          label="Merchant"
          allLabel="All merchants"
          options={sortedMerchants.map((m) => ({ value: m.id, label: m.businessName }))}
          value={draft.merchantId}
          onChange={(v) => set("merchantId", v)}
        />
      )),
      cell: (c) => (
        <MerchantCell merchantId={c.merchant.id} businessName={c.merchant.businessName} publicId={c.merchant.publicId} />
      ),
    },
    {
      key: "label",
      header: "External ID",
      cell: (c) => (
        <RowLink channelId={c.id}>
          <span className="block">{c.label}</span>
          {c.externalId && <span className="block text-xs text-muted-foreground">{c.externalId}</span>}
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      filter: columnFilter(tableState, ["assetId"], (draft, set) => (
        <SelectFilterField
          id="filter-asset"
          label="Asset"
          allLabel="All assets"
          options={(assetOptions ?? []).map((a) => ({
            value: a.id,
            label: `${a.symbol} · ${formatStatusLabel(a.network)}`,
          }))}
          value={draft.assetId}
          onChange={(v) => set("assetId", v)}
        />
      )),
      cell: (c) => (
        <RowLink channelId={c.id}>
          <AssetLabel symbol={c.asset.symbol} network={c.asset.network} />
        </RowLink>
      ),
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status"], (draft, set) => (
        <SelectFilterField
          id="filter-status"
          label="Status"
          allLabel="All statuses"
          options={enumOptions(Object.values(ChannelStatus))}
          value={draft.status}
          onChange={(v) => set("status", v as ChannelStatus | undefined)}
        />
      )),
      cell: (c) => (
        <RowLink channelId={c.id}>
          <StatusBadge label={formatStatusLabel(c.status)} color={CHANNEL_STATUS_COLOR[c.status]} />
        </RowLink>
      ),
    },
    {
      key: "address",
      header: "Address",
      cell: (c) => <HashLink value={c.address} type="address" network={c.asset.network} className="text-xs" />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cell: (c) => <RowLink channelId={c.id}>{formatDate(c.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load channels." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(c) => c.id}
        emptyMessage="No channels match your filters."
      />
    </div>
  );
}
